import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Globe, Layers } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import Header from '@/components/Header';
import CounterAnimation from '@/components/CounterAnimation';
import { Footer } from './HomeFooter';

const caseStudies = [
  {
    id: 'carbon-green-credits',
    client: 'Carbon & Green Credits',
    title: 'Smart Afforestation & Carbon Compliance',
    tag: 'Climate Tech',
    image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    challenge: 'Large-scale afforestation and carbon credit programs require precise site selection, compliance documentation, and ongoing bio-asset monitoring — traditionally manual and error-prone processes spanning multiple regulatory frameworks.',
    solution: 'Built an integrated GIS platform with automated Checker-Maker workflows for optimal zone identification, a Carbon & Compliance Engine for real-time sequestration estimation and PDD generation, bio-asset monitoring for survival rates and growth metrics, and an Analytics Core with temporal filtering and Master Data Management.',
    outcomes: [
      { metric: 'Automated', label: 'PDD generation' },
      { metric: 'Real-time', label: 'Sequestration tracking' },
      { metric: 'GIS-powered', label: 'Zone identification' },
      { metric: 'Centralized', label: 'Species & user governance' },
    ],
  },
  {
    id: 'project-tiger',
    client: 'Wildlife / Project Tiger',
    title: 'AI-Powered Wildlife Conflict Monitoring',
    tag: 'Conservation',
    image: '/ai monitor livestock.png',
    challenge: 'Human-animal conflict zones were reactive and under-resourced. Forest departments lacked real-time visibility into animal movement near populated corridors.',
    solution: 'Deployed a distributed sensor network feeding a centralized AI dashboard. Computer vision models classify species from camera traps and alert ranger teams within 90 seconds of a boundary breach.',
    outcomes: [
      { metric: '90s', label: 'Alert Response Time' },
      { metric: '94%', label: 'Detection Accuracy' },
      { metric: '200+', label: 'Camera Nodes Deployed' },
      { metric: '3x', label: 'Conflict Zone Coverage' },
    ],
  },
  {
    id: 'satsure-carbon',
    client: 'SatSure Forestry',
    title: 'Automated Carbon Compliance Engine',
    tag: 'Climate',
    image: '/satsure.jpg',
    challenge: 'Carbon credit verification required months of manual field surveys and was prone to fraud. Compliance teams lacked scalable tooling to meet growing global demand.',
    solution: 'Built an ML pipeline ingesting multispectral satellite imagery to auto-calculate biomass, canopy coverage, and carbon sequestration — producing audit-ready credit certificates.',
    outcomes: [
      { metric: '10x', label: 'Verification Speed' },
      { metric: '98%', label: 'Audit Accuracy' },
      { metric: '500k Ha', label: 'Forest Area Monitored' },
      { metric: '$12M', label: 'Credits Issued' },
    ],
  },
  {
    id: 'geospoc-flood',
    client: 'Geospoc / Government',
    title: 'Flood Risk Visualization Platform',
    tag: 'Infrastructure',
    image: 'https://images.unsplash.com/photo-1744968777351-074cfcaae034?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxnZW9zcGF0aWFsJTIwZGF0YXxlbnwwfHx8fDE3NzA3MjAwNTl8MA&ixlib=rb-4.1.0&q=85',
    challenge: 'State disaster management agencies had no integrated view of flood-prone zones, drainage networks, and population density — forcing reactive, post-flood responses.',
    solution: 'Developed a government disaster response platform combining hydrological models, satellite elevation data, and real-time weather feeds into a single predictive risk map updated every 6 hours.',
    outcomes: [
      { metric: '6hr', label: 'Forecast Update Cycle' },
      { metric: '85%', label: 'Prediction Accuracy' },
      { metric: '12M+', label: 'People in Coverage Zone' },
      { metric: '5', label: 'States Integrated' },
    ],
  },
  {
    id: 'esri-routing',
    client: 'Esri UK',
    title: 'Intelligent Route Optimization Platform',
    tag: 'Logistics',
    image: '/esri route.jpg',
    challenge: 'Field agent allocation was capped at 60% efficiency due to manual scheduling, territory overlap, and no dynamic rerouting when conditions changed mid-day.',
    solution: 'Built a GIS-powered routing engine that dynamically allocates agents to territories based on demand signals, travel costs, and real-time traffic — with a dispatcher dashboard for overrides.',
    outcomes: [
      { metric: '75%', label: 'Agent Allocation Rate' },
      { metric: '₹10k', label: 'Travel Savings Per Agent' },
      { metric: '25%', label: 'Productivity Gain' },
      { metric: 'UK-wide', label: 'Deployment Scale' },
    ],
  },

  {
    id: 'fpo-enablement',
    client: 'FPO Enablement',
    title: "Women's Empowerment Survey App",
    tag: 'AgriTech',
    image: '/rural empowerment.jpg',
    challenge: "Farmer Producer Organizations needed a scalable operating model that strengthened governance, improved women's participation in leadership, and drove on-ground adoption of digital workflows for procurement, crop planning, and performance tracking.",
    solution: "Delivered an end-to-end FPO enablement platform with member onboarding, crop planning, procurement/aggregation workflows, and performance MIS. Integrated a women's inclusion lens for participation, services access, and decision-making. Combined process re-engineering, capacity building, and lightweight digital workflows.",
    outcomes: [
      { metric: 'Scalable', label: 'Operating model' },
      { metric: 'Improved', label: 'Market linkages' },
      { metric: 'Women-led', label: 'Decision-making' },
      { metric: 'Measurable', label: 'Income & resilience' },
    ],
  },
  {
    id: 'esri-uk-website',
    client: 'ESRI UK',
    title: 'Global Website Upgradation & Management',
    tag: 'Enterprise Web',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    challenge: 'ESRI UK needed to streamline content operations across WordPress and AEM platforms while enabling rapid deployment of campaign-specific microsites with tailored business logic.',
    solution: 'Delivered an enterprise-grade web solution featuring strategic WordPress-AEM integration, custom modules for business logic, and a robust microsite deployment system for campaign management at scale.',
    outcomes: [
      { metric: 'Unified', label: 'WordPress + AEM ops' },
      { metric: 'Custom', label: 'Business modules' },
      { metric: 'Rapid', label: 'Microsite deployment' },
      { metric: 'Scalable', label: 'Campaign management' },
    ],
  },
];
const FILTERS = ['All', 'Conservation', 'Climate', 'Infrastructure', 'Logistics', 'Climate Tech', 'AgriTech', 'Enterprise Web'];

const PLATFORM_IDS = ['maps-guru', 'conv-earth'];

const platforms = [
  {
    id: 'maps-guru',
    name: 'Maps.Guru',
    description: 'Everything you need for beautiful basemaps. Self-host vector tiles, customize styles with AI, and deploy globally.',
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtYXAlMjBiYXNlbWFwJTIwc2F0ZWxsaXRlJTIwdGlsZXN8ZW58MHx8fHwxNzcwNzIwMDAwfDA&ixlib=rb-4.1.0&q=85',
    features: ['Natural language styling', 'Global vector tiles', 'Visual style editor', 'Beautiful presets', 'Drop-in integration', 'Version control'],
  },
  {
    id: 'conv-earth',
    name: 'Conv.Earth',
    description: 'Data conversion, spatialized. Analyze, visualize, and convert proprietary formats to open standards directly in your browser.',
    icon: Layers,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxkYXRhJTIwY29udmVyc2lvbiUyMGdlb3NwYXRpYWwlMjBhbmFseXRpY3N8ZW58MHx8fHwxNzcwNzIwMDAwfDA&ixlib=rb-4.1.0&q=85',
    features: ['AI spatial assistant', 'Topology error detection', 'Area calculations', 'File size optimization', 'Open format conversion', 'Browser-based'],
  },
];

const WorkHero = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="relative pt-40 pb-24 overflow-hidden bg-[#0a0a0b]">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
      </div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-blue-500/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block text-sm text-cyan-400 font-semibold tracking-widest uppercase mb-4">Case Studies</span>
          <h1 className="text-6xl md:text-8xl font-bold mb-6 leading-tight">
            Proof at
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Every Scale
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            From national livestock databases to satellite-powered carbon compliance —
            real outcomes for governments, enterprises, and the planet.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const WorkStatsBar = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-16 bg-[#0f0f10] border-y border-white/5">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { end: 6, suffix: '', label: 'Projects Delivered' },
            { end: 100, suffix: 'M+', label: 'Lives Impacted' },
            { end: 15, suffix: '+', label: 'Countries' },
            { end: 5, suffix: '+', label: 'Gov Deployments' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                <CounterAnimation end={stat.end} suffix={stat.suffix} />
              </div>
              <p className="text-gray-400 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CaseStudyRow = ({ study, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isEven = index % 2 === 0;

  return (
    <motion.div
      id={study.id}
      ref={ref}
      className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0 min-h-[520px] rounded-2xl overflow-hidden border border-white/10 bg-white/5`}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.1 }}
    >
      {/* Image Side */}
      <div className="relative lg:w-5/12 h-72 lg:h-auto overflow-hidden">
        <img
          src={study.image}
          alt={study.title}
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/40 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#0a0a0b]/60" />
        <div className="absolute top-6 left-6">
          <span className="bg-white/10 backdrop-blur-sm border border-white/20 px-3 py-1 rounded-full text-xs text-white font-medium">
            {study.tag}
          </span>
        </div>
      </div>

      {/* Details Side */}
      <div className="lg:w-7/12 p-8 md:p-12 flex flex-col justify-center">
        <p className="text-sm text-cyan-400 font-semibold mb-2 tracking-wide uppercase">{study.client}</p>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">{study.title}</h2>

        <div className="space-y-5 mb-8">
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">Challenge</h4>
            <p className="text-gray-300 leading-relaxed">{study.challenge}</p>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">Solution</h4>
            <p className="text-gray-300 leading-relaxed">{study.solution}</p>
          </div>
        </div>

        {/* Outcomes Grid */}
        <div className="grid grid-cols-2 gap-3">
          {study.outcomes.map((outcome) => (
            <div key={outcome.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-1">
                {outcome.metric}
              </div>
              <p className="text-xs text-gray-400">{outcome.label}</p>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const PlatformRow = ({ platform, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isEven = index % 2 === 0;
  const Icon = platform.icon;

  return (
    <motion.div
      id={platform.id}
      ref={ref}
      className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0 min-h-[480px] rounded-2xl overflow-hidden border border-white/10 bg-white/5`}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.1 }}
    >
      <div className="relative lg:w-5/12 h-72 lg:h-auto overflow-hidden">
        <img
          src={platform.image}
          alt={platform.name}
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/40 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#0a0a0b]/60" />
        <div className="absolute top-6 left-6">
          <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      <div className="lg:w-7/12 p-8 md:p-12 flex flex-col justify-center">
        <span className="inline-block text-sm text-cyan-400 font-semibold tracking-widest uppercase mb-3">Proprietary Platform</span>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{platform.name}</h2>
        <p className="text-gray-300 leading-relaxed text-lg mb-8">{platform.description}</p>

        <div className="grid grid-cols-2 gap-3">
          {platform.features.map((feature) => (
            <div key={feature} className="flex items-center space-x-3 bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const Work = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('projects');
  const pendingScrollId = useRef(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);

    if (PLATFORM_IDS.includes(id)) {
      setActiveTab('platforms');
      pendingScrollId.current = id;
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          const headerHeight = document.querySelector('header')?.offsetHeight ?? 80;
          const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
          window.scrollTo(0, top);
        }
      });
    });
  }, [hash]);

  useEffect(() => {
    if (activeTab !== 'platforms' || !pendingScrollId.current) return;
    const id = pendingScrollId.current;
    pendingScrollId.current = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          const headerHeight = document.querySelector('header')?.offsetHeight ?? 80;
          const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
          window.scrollTo(0, top);
        }
      });
    });
  }, [activeTab]);

  const filtered = activeFilter === 'All'
    ? caseStudies
    : caseStudies.filter(s => s.tag === activeFilter);

  return (
    <div className="bg-[#0a0a0b] text-white overflow-hidden">
      <Header />
      <WorkHero />
      <WorkStatsBar />

      <section className="py-24">
        <div className="container mx-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="bg-white/5 border border-white/10 p-1 rounded-full h-auto gap-1">
                <TabsTrigger
                  value="projects"
                  className="rounded-full px-8 py-2.5 text-sm font-medium transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-none text-gray-300 hover:text-white"
                >
                  Projects
                </TabsTrigger>
                <TabsTrigger
                  value="platforms"
                  className="rounded-full px-8 py-2.5 text-sm font-medium transition-all duration-300 data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-none text-gray-300 hover:text-white"
                >
                  Platforms
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="projects">
              <div className="flex flex-wrap gap-3 justify-center mb-16">
                {FILTERS.map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setActiveFilter(f)}
                    className={`px-5 py-2 rounded-full text-sm font-medium border transition-all duration-300 ${
                      activeFilter === f
                        ? 'bg-white text-black border-white'
                        : 'bg-transparent text-gray-300 border-white/20 hover:border-white/40 hover:text-white'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="popLayout">
                <div className="space-y-12">
                  {filtered.map((study, index) => (
                    <motion.div
                      key={study.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      <CaseStudyRow study={study} index={index} />
                    </motion.div>
                  ))}
                </div>
              </AnimatePresence>

              {filtered.length === 0 && (
                <div className="text-center py-24 text-gray-500">No case studies match this filter.</div>
              )}
            </TabsContent>

            <TabsContent value="platforms">
              <div className="space-y-12">
                {platforms.map((platform, index) => (
                  <PlatformRow key={platform.id} platform={platform} index={index} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Work;
